/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useDeleteOwnerMutation } from '@/hooks/usePlatformDeleteOwner'
import AppDialog, { ModalActions } from '@/components/CustomModal'

const schema = z.object({
  reason: z.string().trim().min(2, 'Raison requise').max(256, 'Max 256'),
  confirm: z.literal('SUPPRIMER', {
    message: 'Tape SUPPRIMER pour confirmer',
  }),
})
type Values = z.infer<typeof schema>

export function DeleteOwnerModal({
  ownerId,
  ownerName,
  disabled,
  onDone,
}: {
  ownerId: string
  ownerName?: string
  disabled?: boolean
  onDone?: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const mut = useDeleteOwnerMutation(ownerId)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '', confirm: '' as unknown as 'SUPPRIMER' },
    mode: 'onChange',
  })

  const closeAndReset = () => {
    setOpen(false)
    setTimeout(() => form.reset({ reason: '', confirm: '' as unknown as 'SUPPRIMER' }), 0)
  }

  const submit = async (v: Values) => {
    try {
      const res = await mut.mutateAsync({ reason: v.reason.trim() })
      toast.success(
        res.already ? 'Compte déjà supprimé' : 'Compte supprimé',
        { description: ownerName ?? undefined },
      )
      closeAndReset()
      onDone?.()
    } catch (e: any) {
      toast.error('Suppression impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        if (!v) closeAndReset()
        else setOpen(true)
      }}
      title='Supprimer le compte'
      description="Le compte est masqué des vues actives. L'email (et l'IP/appareil d'origine si connus) sera bloqué pour empêcher une réinscription. Action irréversible côté client."
      trigger={
        <Button
          variant='destructive'
          size='sm'
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Supprimer le compte
        </Button>
      }
      footer={
        <ModalActions
          cancelLabel='Annuler'
          confirmLabel='Supprimer définitivement'
          confirmVariant='destructive'
          confirmLoading={mut.isPending}
          confirmDisabled={!form.formState.isValid || mut.isPending}
          onConfirm={form.handleSubmit(submit)}
        />
      }
    >
      <Form {...form}>
        <div className='space-y-4'>
          <div className='rounded-lg border p-3 text-sm'>
            <div className='font-medium'>Owner</div>
            <div className='text-muted-foreground'>
              {ownerName ?? '—'} • <span className='font-mono'>{ownerId}</span>
            </div>
          </div>

          <FormField
            control={form.control}
            name='reason'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Raison</FormLabel>
                <FormControl>
                  <Input {...field} autoFocus placeholder='Ex: fraude confirmée' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='confirm'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tape SUPPRIMER pour confirmer</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='SUPPRIMER' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </AppDialog>
  )
}
